import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Loader2 } from "lucide-react";
import type { CierreManualInputs, CierreSituacion } from "@fiscalo/shared-types";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { updateCierreInputs } from "@/services/cierreService";

function money(n: number): string {
  return n.toLocaleString("es-UY", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function parseNum(raw: string): number {
  const n = parseFloat(raw.replace(/\./g, "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

const GROUPS: { title: string; fields: { key: string; label: string }[] }[] = [
  {
    title: "Activo corriente",
    fields: [
      { key: "efectivoPlazaUyu", label: "Efectivo y bancos plaza" },
      { key: "efectivoExteriorUyu", label: "Efectivo y bancos exterior" },
      { key: "cuentasComercialesPlazaUyu", label: "Cuentas comerciales plaza" },
      { key: "cuentasComercialesExteriorUyu", label: "Cuentas comerciales exterior" },
      { key: "otrasCuentasCobrarUyu", label: "Otras cuentas a cobrar" },
      { key: "cuentasSociosActivoUyu", label: "Cuentas socios/directores" },
      { key: "creditosFiscalesUyu", label: "Créditos fiscales" },
      { key: "inventariosUyu", label: "Inventarios" },
      { key: "otrosActivosUyu", label: "Otros activos" },
      { key: "otrosActivosExteriorUyu", label: "Otros activos del exterior" },
    ],
  },
  {
    title: "Activo no corriente",
    fields: [{ key: "intangiblesUyu", label: "Intangibles" }],
  },
  {
    title: "Pasivo corriente",
    fields: [
      { key: "cuentasComercialesPagarUyu", label: "Cuentas comerciales a pagar" },
      { key: "acreedoresImportacionesUyu", label: "Acreedores por importaciones" },
      { key: "acreedoresExteriorUyu", label: "Acreedores del exterior" },
      { key: "anticipoClientesUyu", label: "Anticipo de clientes" },
      { key: "deudasFinancierasBancosUyu", label: "Deudas financieras c/ bancos" },
      { key: "otrasDeudasFinancierasUyu", label: "Otras deudas financieras" },
      { key: "acreedoresFiscalesUyu", label: "Acreedores fiscales" },
      { key: "cuentasSociosPasivoUyu", label: "Cuentas socios/directores" },
      { key: "otrasCuentasPagarUyu", label: "Otras cuentas a pagar" },
    ],
  },
  {
    title: "Pasivo no corriente",
    fields: [
      { key: "deudasFinancierasBancosNcUyu", label: "Deudas financieras c/ bancos" },
      { key: "otrasDeudasFinancierasNcUyu", label: "Otras deudas financieras" },
      { key: "otrasCuentasPagarNcUyu", label: "Otras cuentas a pagar" },
    ],
  },
  {
    title: "Ajustes fiscales / IP",
    fields: [
      { key: "activosExentosUyu", label: "Activos exentos" },
      { key: "ipNoDeducibleUyu", label: "IP no deducible" },
      { key: "itributoVencidoUyu", label: "Itributo vencido" },
      { key: "icosaUyu", label: "ICOSA" },
      { key: "anticiposIpUyu", label: "Anticipos de IP" },
    ],
  },
];

const ALL_KEYS = GROUPS.flatMap((g) => g.fields.map((f) => f.key));

/**
 * Estado de situación (itemizado) → Impuesto al Patrimonio. Bienes de uso
 * vienen del activo fijo; el contador carga el resto del balance línea por
 * línea. El IP sale de patrimonio fiscal × 1,5% con el abatimiento art. 7.
 */
export function SituacionCard({
  tenantId,
  fiscalYear,
  situacion,
  manualInputs,
}: {
  tenantId: string;
  fiscalYear: number;
  situacion: CierreSituacion;
  manualInputs: CierreManualInputs;
}): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const seed = (): Record<string, string> => {
    const s = manualInputs.situacion ?? {};
    const out: Record<string, string> = {};
    for (const k of ALL_KEYS) {
      const v = s[k];
      out[k] = v != null ? String(v) : "";
    }
    return out;
  };
  const [form, setForm] = useState<Record<string, string>>(seed);

  useEffect(() => {
    setForm(seed());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualInputs]);

  const save = useMutation({
    mutationFn: () => {
      const patch: Record<string, number> = {};
      for (const k of ALL_KEYS) patch[k] = parseNum(form[k] ?? "");
      return updateCierreInputs(tenantId, fiscalYear, { situacion: patch });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["accountant", "cierre", tenantId, fiscalYear],
      });
      toast.success(t("cierre.sit.saved", "Situación actualizada"));
    },
    onError: () => toast.error(t("cierre.sit.error", "No pudimos guardar")),
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">
          {t("cierre.sit.title", "Estado de situación — Impuesto al Patrimonio")}
        </CardTitle>
        <CardDescription>
          {t(
            "cierre.sit.description",
            "Bienes de uso vienen del activo fijo. Cargá el resto del balance. Montos en UYU.",
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {GROUPS.map((group) => (
          <div key={group.title} className="space-y-2">
            <h4 className="text-sm font-semibold text-foreground">
              {group.title}
            </h4>
            <div className="grid gap-3 sm:grid-cols-2">
              {group.fields.map((f) => (
                <div key={f.key} className="grid gap-1.5">
                  <Label htmlFor={`sit-${f.key}`} className="text-xs">
                    {f.label}
                  </Label>
                  <Input
                    id={`sit-${f.key}`}
                    inputMode="decimal"
                    className="h-8 tabular-nums"
                    value={form[f.key] ?? ""}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, [f.key]: e.target.value }))
                    }
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
        <div className="flex justify-end">
          <Button
            type="button"
            size="sm"
            onClick={() => save.mutate()}
            disabled={save.isPending}
          >
            {save.isPending ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : null}
            {t("common.save", "Guardar")}
          </Button>
        </div>

        <dl className="grid gap-1 rounded-md border bg-muted/30 p-3 text-sm">
          <Row label={t("cierre.sit.bienesContable", "Bienes de uso (neto contable)")} value={money(situacion.bienesDeUsoNetoContableUyu)} />
          <Row label={t("cierre.sit.activoContable", "Total activo (contable)")} value={money(situacion.activoContableTotalUyu)} />
          <Row label={t("cierre.sit.pasivoContable", "Total pasivo (contable)")} value={`(${money(situacion.pasivoContableTotalUyu)})`} />
          <Row label={t("cierre.sit.patrimonioContable", "Patrimonio contable")} value={money(situacion.patrimonioContableUyu)} strong />
          <div className="my-1 border-t" />
          <Row label={t("cierre.sit.activoComputable", "Activo computable")} value={money(situacion.activoComputableUyu)} />
          <Row label={t("cierre.sit.pasivoComputable", "Pasivo computable")} value={`(${money(situacion.pasivoComputableUyu)})`} />
          <Row label={t("cierre.sit.patrimonioFiscal", "Patrimonio fiscal")} value={money(situacion.patrimonioFiscalUyu)} strong />
          <Row label={t("cierre.sit.ip", "IP (1,5%)")} value={money(situacion.ipUyu)} />
          <Row label={t("cierre.sit.abatimiento", "Abatimiento")} value={`(${money(situacion.abatimientoUyu)})`} />
          <Row label={t("cierre.sit.anticipos", "Anticipos de IP")} value={`(${money(situacion.anticiposUyu)})`} />
          <div className="mt-1 flex items-center justify-between border-t pt-1 text-base font-semibold">
            <span>{t("cierre.sit.saldo", "Saldo IP a pagar")}</span>
            <span className="tabular-nums">$U {money(situacion.saldoUyu)}</span>
          </div>
          <Row
            label={t("cierre.sit.anticipoProx", "Anticipo próx. ejercicio")}
            value={money(situacion.anticipoProxUyu)}
          />
        </dl>
      </CardContent>
    </Card>
  );
}

function Row({
  label,
  value,
  strong,
}: {
  label: string;
  value: string;
  strong?: boolean;
}): JSX.Element {
  return (
    <div
      className={
        "flex items-center justify-between gap-2" + (strong ? " font-medium" : "")
      }
    >
      <span className={strong ? "text-foreground" : "text-muted-foreground"}>
        {label}
      </span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}
